// F18 (FR-API3): the public API reference at /developers. Rendered in-app from the server's own
// /api/v1/openapi.json with design-system components, so it is self-hosted by construction: no
// CDN script, font or stylesheet (the brief allows a static Redoc/Scalar bundle; this keeps the
// web bundle free of a second UI framework and passes lint:colors/check-icons like every page).
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Badge, Callout, Card, Wordmark } from "@grackle/ui";
import { api, ApiFail } from "../api/client";
import "./developers.css";

type SecurityRequirement = Record<string, string[]>;

interface Operation {
  summary?: string;
  description?: string;
  tags?: string[];
  security?: SecurityRequirement[];
}

export interface OpenApiDoc {
  info?: { title?: string; version?: string; description?: string };
  paths?: Record<string, Record<string, Operation>>;
}

export interface OperationRow {
  method: string;
  path: string;
  tag: string;
  summary: string;
  description: string;
  access: string;
}

const METHODS = ["get", "post", "put", "patch", "delete", "head"];

/** How a token may call this operation: its scope, or "Session only" when no bearer alternative exists. */
export function accessLabel(security: SecurityRequirement[] | undefined): string {
  if (!security || security.length === 0) return "Public";
  const bearer = security.find((r) => "bearerToken" in r);
  if (!bearer) return "Session only";
  const scopes = bearer.bearerToken ?? [];
  return scopes.length ? `Token: ${scopes.join(", ")}` : "Token";
}

/** Flatten the document into one row per operation, grouped by tag then path. */
export function operationRows(doc: OpenApiDoc): OperationRow[] {
  const rows: OperationRow[] = [];
  for (const [path, item] of Object.entries(doc.paths ?? {})) {
    for (const method of METHODS) {
      const op = item[method];
      if (!op) continue;
      rows.push({
        method: method.toUpperCase(),
        path,
        tag: op.tags?.[0] ?? "other",
        summary: op.summary ?? "",
        description: op.description ?? "",
        access: accessLabel(op.security),
      });
    }
  }
  rows.sort((a, b) => a.tag.localeCompare(b.tag) || a.path.localeCompare(b.path) || a.method.localeCompare(b.method));
  return rows;
}

export default function DevelopersPage() {
  const [doc, setDoc] = useState<OpenApiDoc | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api<OpenApiDoc>("GET", "/openapi.json")
      .then(setDoc)
      .catch((err) => setError(err instanceof ApiFail ? err.message : "Couldn't load the API reference."));
  }, []);

  const groups = useMemo(() => {
    const out = new Map<string, OperationRow[]>();
    for (const row of doc ? operationRows(doc) : []) {
      const list = out.get(row.tag) ?? [];
      list.push(row);
      out.set(row.tag, list);
    }
    return [...out.entries()];
  }, [doc]);

  return (
    <div className="developers-shell">
      <header className="developers-topbar">
        <Link to="/" aria-label="Grackle home">
          <Wordmark />
        </Link>
        <span className="developers-topbar__spacer" />
        <Link to="/account?tab=tokens">Access tokens</Link>
        <a href="/api/v1/openapi.json">openapi.json</a>
      </header>
      <main className="developers-body">
        <h1 className="developers-title">Grackle API</h1>
        <p className="developers-lede">
          REST under <span className="mono">/api/v1</span>. Send a personal or organization token as{" "}
          <span className="mono">Authorization: Bearer &lt;token&gt;</span>. A <b>read</b> token may call GET and HEAD;
          a <b>write</b> token may call everything a token is allowed to. Session-only routes (sign-in, MFA, token
          minting, SSO setup, deletion, the ops console) refuse tokens.
        </p>
        <p className="developers-lede">
          Every response carries <span className="mono">RateLimit-Limit</span>,{" "}
          <span className="mono">RateLimit-Remaining</span> and <span className="mono">RateLimit-Reset</span>; a 429
          adds <span className="mono">Retry-After</span>. Errors are always{" "}
          <span className="mono">{"{\"error\":{\"code\",\"message\"}}"}</span>. Paths under{" "}
          <span className="mono">/api/v1</span> only change in backward-compatible ways. Generate a client with
          openapi-generator from <span className="mono">/api/v1/openapi.json</span>.
        </p>
        {error && <Callout tone="danger">{error}</Callout>}
        {!doc && !error && <p className="developers-lede">Loading…</p>}
        {doc && (
          <p className="developers-meta mono">
            {doc.info?.title} {doc.info?.version} · {groups.reduce((n, [, rows]) => n + rows.length, 0)} operations
          </p>
        )}
        {groups.map(([tag, rows]) => (
          <Card key={tag} title={tag} className="developers-group">
            <ul className="developers-ops" aria-label={`${tag} operations`}>
              {rows.map((row) => (
                <li key={`${row.method} ${row.path}`} className="developers-op">
                  <Badge size="sm" tone={row.method === "GET" ? "accent" : "neutral"} className="developers-op__method">
                    {row.method}
                  </Badge>
                  <span className="mono developers-op__path">{row.path}</span>
                  <span className="developers-op__summary">{row.summary}</span>
                  <Badge size="sm" tone="outline">
                    {row.access}
                  </Badge>
                </li>
              ))}
            </ul>
          </Card>
        ))}
      </main>
    </div>
  );
}
